import { Action, isType } from "typescript-fsa";
import update from "immutability-helper";
import { isEqual } from "lodash";
import log from "loglevel";
import { Map } from "immutable";
import uuid from "uuid";

import { isFailedResponseUnauthenticatedNotLogin } from "@api/actions";
import { logout } from "@auth/actions";
import { getArrayParamFromPagination, makeMapLoadedModel, makeMergeEntities, PaginationResult } from "@orm/models";
import * as actions from "../actions";
import { REPORT_TYPE_STATEMENT_IDS } from "../constants";
import {
    Report,
    ReportType,
    ReportStatus,
    ReportsNormalisedEntities,
    ReportsInternalState as State,
    ReportKeys,
} from "../models";

const getInitialState = () => ({
    entities: {
        reports: Map<number, Report>(),
        reportStatuses: Map<string, ReportStatus>(),
        reportTypes: Map<number, ReportType>(),
    },
    statements: {
        list: [],
        pagination: null,
        lastLoaded: null,
    },
    customReports: {
        list: [],
        pagination: null,
        lastLoaded: null,
        requestId: uuid(),
    },
} as State);

const mapLoadedReport = makeMapLoadedModel<Report, number>("report", Report);
const mergeEntities = makeMergeEntities<State, ReportsNormalisedEntities>(
    ["reports", "reportStatuses", "reportTypes"],
    { reports: mapLoadedReport },
);

const isPaginationForStatements = (pagination: PaginationResult) => {
    const typeIdsStr = getArrayParamFromPagination(pagination, "type_ids");
    if (typeIdsStr == null) {
        return false;
    }
    const typeIds = typeIdsStr.map((id) => parseInt(id, 10)).sort();
    return isEqual(typeIds, REPORT_TYPE_STATEMENT_IDS);
};

function reduce(state: State = getInitialState(), action: actions.ReportsAction): State {
    // State should never be null...
    if (state === null) {
        log.warn("reports state setting to null, resetting to initialState.");
        state = {} as State;
        state = update(state, { $set: getInitialState() });
    }

    if (isType(action, actions.reportsIndexSuccess)) {
        // Successful reportsIndex
        return reportsIndexSucces(state, action);
    }
    else if (isType(action, actions.reportsIndexFailure)) {
        // TODO Handle errors
    }
    else if (isType(action, actions.reportsStoreSuccess)) {
        // Successful reportsStore
        return addReport(state, action.payload);
    }
    else if (isType(action, actions.reportTypesIndexSuccess)) {
        // Successful reportTypesIndex
        return mergeEntities(state, action.payload.entities);
    }
    else if (isType(action, logout) || isFailedResponseUnauthenticatedNotLogin(action)) {
        // Clear the state on logout.
        return update(state, { $set: getInitialState() });
    }
    // Otherwise we do nothing
    return state;
}

function addReport(state: State, report: Report): State {
    // Add the report to the map
    const newState = {
        entities: {
            reports: state.entities.reports.set(report.id, report),
        },
        customReports: {
            list: [...state.customReports.list],
        },
    } as State;
    newState.customReports.list.unshift(report.id);
    return update(state, {
        entities: { reports: { $set: newState.entities.reports } },
        customReports: {
            list: { $set: newState.customReports.list },
            lastLoaded: { $set: null },
            requestId: { $set: uuid() },
        },
    });
}

function reportsIndexSucces(
    state: State,
    action: Action<[PaginationResult, actions.ReportsResult]>,
): State {
    const pagination = action.payload[0];
    const { result, entities } = action.payload[1];
    // If the pagination is the first page, replace all the reports.
    const key: ReportKeys = isPaginationForStatements(pagination)
        ? "statements"
        : "customReports";
    let fullList = state[key].list.concat(result);
    if (pagination.page === 1) {
        fullList = result;
    }
    const newState = update(state, {
        [key]: {
            $set: {
                pagination,
                list: fullList,
                lastLoaded: new Date(),
            },
        },
    });

    return mergeEntities(newState, entities, "report");
}

export { reduce as reports, getInitialState };
